import { createContext, useState } from "react";

import utilService from "../services/util.service";
import inboxService from "../services/inbox.service";
import { ensureFilePathExtension, getMimeType } from "../utils/getMimeType";

export const UtilContext = createContext();
export const UtilProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  // Google Auto Complete
  const [predictions, setPredictions] = useState([]);
  const [place, setPlace] = useState({});
  const [imageUrls, setImageUrls] = useState([]);
  const [imageUrl, setImageUrl] = useState("");
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [pmsFields, setPMSFields] = useState(null);

  const [socket, setSocket] = useState(null);
  const [notification, setNotification] = useState(null);

  const [counts, setCounts] = useState({});
  const [dnsErrors, setDNSErrors] = useState([]);
  const [notified, setNotified] = useState([]);

  const [userStatus, setUserStatus] = useState({});

  let utilContext = {
    loading,
    imageUrls,
    imageUrl,
    pmsFields,
    dnsErrors,
    counts,

    predictions,
    setPredictions,
    notification,
    setNotification,

    socket,
    setSocket,
    userStatus,
    setUserStatus,

    // Generates a random UUID
    genUUID: () => {
      return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
        (
          c ^
          (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
        ).toString(16),
      );
    },

    // Google Auto Complete
    autoComplete: async (input, location = "") => {
      let result = await utilService.autoComplete(input, location);
      let { results, error } = result.data;
      if (error) {
        console.log(error);
      } else {
        setPredictions(results.predictions);
      }
    },
    // Google Places
    place: async (google_place_id) => {
      let result = await utilService.place(google_place_id);
      let { results, error } = result.data;
      if (error) {
        console.log(error);
      } else {
        setPlace(results.result);
        return results.result;
      }
    },
    //Google Images
    images: async (google_place_ids) => {
      let temp = google_place_ids?.map(async (id) => {
        let result = await utilService.images(id);
        let { google_place_id, url } = result.data;
        return { google_place_id, url };
      });
      Promise.all(temp).then((res) => {
        setImageUrls(res);
      });
    },
    image: async (google_place_id) => {
      let result = await utilService.images(google_place_id);
      let { results, error } = result.data;
      if (error) {
        throw error;
      } else {
        setImageUrl(results);
        return results;
      }
    },
    //uploaded Url
    uploadedUrl,
    //Upload image to S3
    uploadFile: async (file, where, opts = {}) => {
      const normalizedMime = getMimeType(file);
      const filename = ensureFilePathExtension(where, file);
      let uploadObject = {
        filename,
        mime: normalizedMime,
        ...opts,
      };
      const result = await utilService.uploadFile(uploadObject);
      const { error, url } = result.data;
      if (error) {
        throw error;
      } else {
        let headers = new Headers();
        headers.append("Content-Type", normalizedMime);

        let options = {
          method: "PUT",
          headers: headers,
          body: file,
          redirect: "follow",
        };

        let response = await fetch(url, options)
          .then((response) => {
            return response;
          })
          .catch((error) => {
            throw error;
          });

        if (!response.ok) {
          const uploadError = new Error(
            `Upload failed with status ${response.status}`,
          );
          uploadError.status = response.status;
          throw uploadError;
        }

        let uploadedUrl = url;
        let realUrl = uploadedUrl.substring(0, uploadedUrl.indexOf("?"));
        setUploadedUrl(realUrl);
        return realUrl;
      }
    },
    getPmsFields: async () => {
      setLoading(true);
      const resp = await utilService.getPmsFields();
      setPMSFields(resp.data);
      setLoading(false);
    },

    getNotification: async (msg) => {
      if (!!msg?.thread_id) {
        let response = await inboxService.getThreadOverview(msg.thread_id);

        let { error, thread } = response.data;
        let { inbox_id, thread_id } = thread;
        let { message_id, sender_type, sender, content } = thread.last_msg;

        if (Array.isArray(sender)) {
          sender = sender[sender.length - 1];
        }

        let formatted = {
          headline: `New message from ${sender?.first_name || ""} ${sender?.last_name || ""}`,
          inbox_id: inbox_id,
          message_id: message_id,
          message_type: sender_type,
          message_content: content,
          sender_name: `${sender?.first_name || ""} ${sender?.last_name || ""}`,
          thread_id: thread_id,
        };

        if (!!thread && notification?.message_id !== message_id) {
          setNotification(formatted);
        }
      }
    },

    /**
     Read is a list of all the threads a user has viewed this session
     Viewing is set when a user is viewing a thread, but the message box is empty
     Typing is set when a user is viewing a thread AND the message box is populated
     Browsing just means that the user is not viewing a thread
    **/
    setStatus(user, message) {
      let { action, data } = message;

      let status = userStatus[data.personal_inbox_id] || {
        viewing: null,
        typing: null,
        read: [],
      };

      if (data.status === "viewing") {
        status.viewing = +data.thread_id;
        status.typing = null;

        if (!status.read.includes(data.thread_id)) {
          status.read.push(+data.thread_id);
        }
      }

      if (data.status === "browsing") {
        status.viewing = null;
        status.typing = null;
      }

      if (data.status === "typing") {
        status.viewing = +data.thread_id;
        status.typing = +data.thread_id;
      }

      userStatus[data.personal_inbox_id] = status;

      console.log(
        "Setting User Status",
        message,
        JSON.parse(JSON.stringify(userStatus)),
      );
      setUserStatus({ ...userStatus });

      if (action === "echo_status") {
        let pid = (user?.user?.personal_inboxes || [])[0]?.id || null;
        if (!pid) {
          return;
        }

        let msg = {
          thread_id: null,
          personal_inbox_id: pid,
          status: "",
        };

        let current = userStatus[pid] || {};

        if (!current.viewing && !current.typing) {
          msg.status = "browsing";
        }

        if (!!current.viewing && !current.typing) {
          msg.status = "viewing";
          msg.thread_id = current.viewing;
        }

        if (!!current.viewing && !!current.typing) {
          msg.status = "typing";
          msg.thread_id = current.typing;
        }

        utilService.pushToRoom("update_status", msg);
      }
    },

    shareGuide: async (obj) => {
      const response = await utilService.shareGuide(obj);
      const { error, thread_id, success } = response.data;

      if (!!thread_id || !!success) {
        return thread_id || success;
      }
    },

    shareMessage: async (msg_type, msg_id, contact) => {
      const response = await utilService.shareMessage(
        msg_type,
        msg_id,
        contact,
      );

      const { error, thread_id } = response.data;

      if (!!thread_id) {
        return thread_id;
      }
    },

    emailDNSCheck: async () => {
      let response = await utilService.emailDNSCheck();

      let errorCodes = {
        dkim1: (domain) => {
          return `Missing DKIM record for ${domain}. Please please add a CNAME record with the 'host' value 'mte1._domainkey.${domain}' and the 'answer' value 'dkim1.mandrillapp.com'`;
        },
        dkim2: (domain) => {
          return `Missing DKIM record for ${domain}. Please please add a CNAME record with the 'host' value 'mte2._domainkey.${domain}' and the 'answer' value 'dkim2.mandrillapp.com'`;
        },
        dmarc: (domain) => {
          return `Missing DMARC record for ${domain}. Please add or update a TXT record in your domain's DNS settings settings with 'host' value '_dmarc.${domain}' and the 'answer' value 'v=DMARC1; p=none;'`;
        },
      };

      let errors = [];
      let { domains = [], error = null } = response.data;
      if (!!error) {
        console.log("DNS Check Error:", error);
      } else {
        // console.log(response);

        Object.keys(domains).map((key) => {
          let domain = domains[key];

          for (var x in domain) {
            if (!!domain[x].code) {
              errors.push(errorCodes[x](key));
            }
          }
        });
      }

      setDNSErrors(errors);
    },
  };

  return (
    <UtilContext.Provider value={utilContext}>{children}</UtilContext.Provider>
  );
};
